import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowBack,
  IconAttach,
  IconDelete,
  IconMore,
} from "../components/CustomIcons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import defaultTeamImage from "../assets/images/defaultTeamPic.svg";
import {
  deleteLeagueTeams,
  getLeagueBySlug,
  Team,
  updateLeagueProgressStep,
} from "../components/leagueLocker/firestoreQuery";
import { useAuth } from "../contexts/AuthContext";
import { useClickOutside } from "../hooks/useClickOutside";
import { encryptObject } from "../components/utils/crypto";
import { SectionLoader } from "../components/ui/SectionLoader";

const ManageTeamPage = () => {
  const { leagueSlug } = useParams(); //Edit mode
  const navigate = useNavigate();
  const user = useAuth().user;
  const [leagueData, setLeagueData] = useState<any>({});
  const [leagueId, setLeagueId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [teamList, setTeamList] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [openDeleteOption, setOpenDeleteOption] = useState(false);
  const deleteOptionRef = useRef<HTMLDivElement>(null);
  const [inviteButtonText, setInviteButtonText] = useState<string | null>(
    "Invite"
  );
  const [showCanNotDeleteModal, setShowCanNotDeleteModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Automatically close delete option when clicking outside
  useClickOutside(deleteOptionRef, () => {
    if (openDeleteOption) {
      setOpenDeleteOption(false);
    }
  });

  const handleInvite = async () => {
    const url = window.location.href;

    const payload = { isEncoded: true, slug: leagueData.leagueUrl };
    const token = encryptObject(payload);
    const inviteLink = `${window.location.origin}/${token}`;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteButtonText("Link Copied");
      await updateLeagueProgressStep(leagueData.id, "invite-other-teams");
      setTimeout(() => {
        setInviteButtonText("Invite");
      }, 3000);
    } catch (err) {
      // fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    const fetchLeagueDetails = async () => {
      if (leagueSlug) {
        try {
          setIsLoading(true);
          const res = await getLeagueBySlug(leagueSlug);
          if (res) {
            if (res.createdBy !== user?.uid) {
              navigate("/dashboard");
              return;
            } else {
              // Populate state with fetched data
              setLeagueData(res);
              setTeamList(res.teams || []);
              setLeagueId(res.id || "");
            }
          } else {
            console.error("No league found with the provided slug.");
            navigate("/dashboard");
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error fetching league:", error);
          setIsLoading(false);
          //setErrorText(error as string);
        }
      }
      setIsLoading(false);
    };
    fetchLeagueDetails();
  }, [leagueSlug]);

  // Check if a creator already has a published challenge in this league
  const hasPublishedChallenge = (creatorId?: string) => {
    if (!creatorId) return false;
    return !!leagueData?.challenges?.some(
      (ch: any) =>
        ch?.createdBy === creatorId &&
        ch?.challengeCreationStatus === "published"
    );
  };

  // Check if the team has joined any challenge (challenge.teams contains the team with hasJoined true)
  const hasJoinedAChallenge = (team: Team | null) => {
    if (!team) return false;
    return !!leagueData?.challenges?.some((ch: any) =>
      ch?.teams?.some(
        (t: Team) => t?.createdBy === team.createdBy && t?.hasJoined === true
      )
    );
  };

  const handleTeamDeletionConfirmed = async () => {
    if (selectedTeam) {
      // can't delete if they have a published challenge OR they have joined a challenge
      const canNotDelete =
        hasPublishedChallenge(selectedTeam?.createdBy) ||
        hasJoinedAChallenge(selectedTeam);
      setShowCanNotDeleteModal(canNotDelete);
      if (!canNotDelete) {
        const updatedTeamList = teamList.filter(
          (team) => team.createdBy !== selectedTeam.createdBy
        );
        setTeamList(updatedTeamList);
        try {
          setIsSubmitting(true);
          await deleteLeagueTeams(
            leagueId,
            updatedTeamList,
            selectedTeam?.createdBy ?? ""
          );
        } catch (e) {
          console.log("Error deleting teams:", e);
        } finally {
          setSelectedTeam(null);
          setIsSubmitting(false);
          setShowConfirmDeleteModal(false);
        }
      }
    }
  };

  const handleTeamDeletion = () => {
    if (selectedTeam) {
      // can't delete if they have a published challenge OR they have joined a challenge
      const canNotDelete =
        hasPublishedChallenge(selectedTeam?.createdBy) ||
        hasJoinedAChallenge(selectedTeam);

      if (canNotDelete) {
        setShowCanNotDeleteModal(true);
      } else {
        setShowConfirmDeleteModal(true);
      }
    }
  };

  const canDelete = () => {
    return selectedTeam?.createdBy !== user?.uid;
  };

  const checkEligibility = () => {
    if (leagueData?.teams?.length >= +leagueData?.leagueSize) {
      return false;
    }
    return true;
  };

  if (isLoading) {
    return <SectionLoader />;
  }

  return (
    <div className="pageOuterWrapper">
      <Navbar />
      <div className="contentArea otherContentArea">
        <div className="wrapper">
          <div className="backWrap">
            <Link to={`/manage-league/${leagueSlug}`}>
              <ArrowBack /> Back
            </Link>
          </div>
          <form>
            <div className="yourLeagueHeader">
              <div className="headingItem">Manage Teams</div>
              <div className="actionItems">
                <button
                  type="button"
                  className="btnStyle btnFilled"
                  onClick={() => navigate(`/manage-league/${leagueSlug}`)}
                >
                  SAVE
                </button>
              </div>
            </div>
            {checkEligibility() && leagueData?.createdBy === user?.uid && (
              <div className="topBtnWrap" onClick={handleInvite}>
                <button type="button" className="btnStyle">
                  {inviteButtonText}
                  <IconAttach />
                </button>
              </div>
            )}
            <div className="shadowBlk">
              <div className="blkHeading">Team Name</div>
              <ul className="teamList">
                {teamList &&
                  teamList?.map((team) => (
                    <li key={team?.createdBy} className="teamItem">
                      <div className="teamDetail">
                        <span
                          className="teamPic"
                          style={{ border: "1px solid #ccc" }}
                        >
                          <img
                            src={team?.imageUrl ?? defaultTeamImage}
                            alt=""
                          />
                        </span>
                        <span>{team?.name}</span>
                      </div>

                      {team?.createdBy !== user?.uid && (
                        <div className="moreActionWrap">
                          <span
                            className="actionOption"
                            onClick={() => {
                              setSelectedTeam(team);
                              setOpenDeleteOption((prev) =>
                                selectedTeam?.createdBy === team?.createdBy
                                  ? !prev
                                  : true
                              );
                            }}
                          >
                            <IconMore />
                          </span>
                          {canDelete() &&
                            selectedTeam?.createdBy === team?.createdBy &&
                            openDeleteOption && (
                              <div
                                className="moreOptionWrap"
                                ref={deleteOptionRef}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleTeamDeletion();
                                    setOpenDeleteOption(false);
                                  }}
                                >
                                  <IconDelete />
                                  <span>Delete Team</span>
                                </button>
                              </div>
                            )}
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
            <button
              type="button"
              className="btnStyle btnFilled btnFull"
              onClick={() => navigate(`/manage-league/${leagueSlug}`)}
            >
              SAVE
            </button>
          </form>
        </div>
        {showConfirmDeleteModal && (
          <div className="modalWrap">
            <div className="modalContent">
              <p>
                Are you sure you want to delete{" "}
                <strong>{selectedTeam?.name}</strong>?
              </p>
              <div className="btnWrap">
                <button
                  className="btnStyle"
                  type="button"
                  onClick={() => {
                    setShowConfirmDeleteModal(false);
                    setSelectedTeam(null);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="btnStyle btnFilled"
                  type="button"
                  onClick={handleTeamDeletionConfirmed}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showCanNotDeleteModal && (
          <div className="modalWrap">
            <div className="modalContent">
              <p>
                Unable to delete <strong>{selectedTeam?.name} </strong> — team
                is already associated with a challenge.
              </p>

              <button
                className="btnStyle bigBtn"
                type="button"
                onClick={() => {
                  setShowCanNotDeleteModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ManageTeamPage;
